import { createElement, useCallback, useRef } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ScheduleReviewUndoToast } from "./ScheduleReviewUndoToast";
import { SCHEDULE_REVIEW_TOAST_KEY } from "./scheduleReview.constants";

interface UseScheduleReviewDismissParams<TId> {
    dismiss: (ids: TId[]) => Promise<TId[]>;
    restore: (ids: TId[]) => Promise<TId[]>;
    noun: string;
    onSettled?: () => void;
}

/**
 * Dismiss-with-undo for review rows. Undo is ignored while a restore is in
 * flight, and the toast replaces itself in the same slot once it settles.
 */
export function useScheduleReviewDismiss<TId>({
    dismiss,
    restore,
    noun,
    onSettled,
}: UseScheduleReviewDismissParams<TId>) {
    const { toast, error } = useToaster();
    const isRestoringRef = useRef(false);

    const undo = useCallback(
        async (ids: TId[]) => {
            if (isRestoringRef.current) {
                return;
            }
            isRestoringRef.current = true;
            const restoreRows = async () => {
                try {
                    const restored = await restore(ids);
                    toast(
                        `Restored ${pluralizeWithCount(noun, restored.length)}`,
                        SCHEDULE_REVIEW_TOAST_KEY,
                    );
                } catch (restoreError) {
                    error(
                        restoreError instanceof Error
                            ? restoreError.message
                            : `Failed to restore ${noun}`,
                    );
                }
            };
            await restoreRows().finally(() => {
                isRestoringRef.current = false;
                onSettled?.();
            });
        },
        [error, noun, onSettled, restore, toast],
    );

    return useCallback(
        async (ids: TId[]) => {
            if (ids.length === 0) {
                return;
            }
            const dismissRows = async () => {
                try {
                    const dismissed = await dismiss(ids);
                    toast(
                        createElement(ScheduleReviewUndoToast, {
                            message: `Dismissed ${pluralizeWithCount(noun, dismissed.length)}`,
                            onUndo: () => void undo(dismissed),
                        }),
                        SCHEDULE_REVIEW_TOAST_KEY,
                    );
                } catch (dismissError) {
                    error(
                        dismissError instanceof Error
                            ? dismissError.message
                            : `Failed to dismiss ${noun}`,
                    );
                }
            };
            await dismissRows().finally(() => {
                onSettled?.();
            });
        },
        [dismiss, error, noun, onSettled, toast, undo],
    );
}
